// @flow

import {
  type IncomingPayment,
  type OpenedIncomingPayment,
  getAddress,
  getCustomerEmail,
  getCustomerName,
  getDocument,
  getPhoneNumber,
  getReceipts,
  getPaymentType,
  getPaymentTypeCode,
  getCountry,
  getPersonType,
} from '@tectonics/prs/incoming-payment';

import {
  getZipcode,
  getState as getAddressState,
  getCity,
  getStreetName,
  getStreetNumber,
  getStreetComplement,
} from '@tectonics/prs/address';

import { getDocumentTypeInferredFromValue as getBrazilianDocumentTypeInferredFromValue } from '@tectonics/prs/customer/brazil';
import { getLowerInstalmentKey } from '@tectonics/prs/receipts';
import type { UserInputValues } from '@tectonics/prs/user-input-values/types';
import { decideArgentineDocument, filterArgentineCashBankCode } from '@tectonics/prs/argentina';
import { peruvianDocumentTypeInferredFromValue } from '@tectonics/prs/peru/peruvian-document-type-support';
import { formatValueWithMask } from '@tectonics/components/input-mask';
import { TURKEY_PHONE_MASK } from '@tectonics/commons/country/countries/turkey';

export function getOpenedPaymentBasedInitialUserInputValues(incomingPayment: OpenedIncomingPayment): UserInputValues {
  return {
    ...getOpenedCommonsInitialValues(incomingPayment),
    ...getCountryInitialValues(incomingPayment),
  };
}

function getOpenedCommonsInitialValues(incomingPayment: OpenedIncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: '',
    customerName: getCustomerName(incomingPayment),
    customerEmail: getCustomerEmail(incomingPayment) || '',
    cardHolderName: '',
    cardNumber: '',
    cardExpiryDate: '',
    cardSecurityCode: '',
    selectedInstalment: getLowerInstalmentKey(getReceipts(incomingPayment)),
    acceptVisaTerms: true,
    selectedBankCode: '',
    selectedTefBank: '',
    selectedSafetypayOption: getInitialSafetypayOption(incomingPayment),
    selectedCryptocurrency: '',
    selectedProtocol: '',
  };
}

function getInitialSafetypayOption(incomingPayment: IncomingPayment): string {
  const paymentTypeCode = getPaymentTypeCode(incomingPayment);

  return (paymentTypeCode === 'safetypay-cash' || paymentTypeCode === 'safetypay-online')
    ? paymentTypeCode
    : '';
}

function getCountryInitialValues(incomingPayment: OpenedIncomingPayment): UserInputValues {
  const maskValue = function(value: string, country: string): string {
    switch (country) {
      case 'ke':
        return `+254 ${value.substring(3)}`;
      case 'gh':
        return `+233 ${value.substring(3)}`;
      case 'ug':
        return `+256 ${value.substring(3)}`;
      case 'ng':
        return `+234 ${value.substring(3)}`;
      case 'zm':
        return `+260 ${value.substring(3)}`;
      case 'tr':
        return formatValueWithMask(`+90 ${value.substring(2)}`, TURKEY_PHONE_MASK);

      default:
        return '';
    }
  };

  switch (getCountry(incomingPayment)) {
    case 'br':
      return {
        brazilianDocument: getDocumentOrEmpty(incomingPayment),
        brazilianDocumentType: getBrazilianDocumentTypeInferredFromValue(getDocument(incomingPayment)),
        brazilianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        brazilianZipcode: getZipcodeOrEmpty(incomingPayment),
        brazilianState: getAddressState(getAddress(incomingPayment)),
        brazilianCity: getCity(getAddress(incomingPayment)),
        brazilianAddress: getStreetName(getAddress(incomingPayment)),
        brazilianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        brazilianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEWallet: '',
        mealVoucherVrPreTokenizedCardCvv: '',
      };

    case 'mx':
      return {
        mexicanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        mexicanZipcode: getZipcodeOrEmpty(incomingPayment),
        mexicanState: getAddressState(getAddress(incomingPayment)),
        mexicanCity: getCity(getAddress(incomingPayment)),
        mexicanAddress: getStreetName(getAddress(incomingPayment)),
        mexicanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        mexicanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEWallet: 'mercadopago',
      };

    case 'ar': {
      const argentineDocument = getDocumentOrEmpty(incomingPayment);

      return {
        argentineDocument: argentineDocument,
        argentineCbu: '',
        companyName: '',
        argentineDocumentType: decideArgentineDocument(argentineDocument),
        argentinePhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        argentineZipcode: getZipcodeOrEmpty(incomingPayment),
        argentineState: getAddressState(getAddress(incomingPayment)),
        argentineCity: getCity(getAddress(incomingPayment)),
        argentineAddress: getStreetName(getAddress(incomingPayment)),
        argentineStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        argentineStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedBankCode: filterArgentineCashBankCode(getPaymentTypeCode(incomingPayment)),
        selectedEWallet: getSelectedEWallet(incomingPayment),
        acceptTerms: false,
      };
    }

    case 'co': {
      const personType = getPersonType(incomingPayment);

      return {
        colombianDocument: getDocumentOrEmpty(incomingPayment),
        colombianDocumentType: personType === 'business' ? 'NIT' : 'CC',
        colombianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        colombianZipcode: getZipcodeOrEmpty(incomingPayment),
        colombianState: getAddressState(getAddress(incomingPayment)),
        colombianCity: getCity(getAddress(incomingPayment)),
        colombianAddress: getStreetName(getAddress(incomingPayment)),
        colombianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        colombianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEftBank: '',
        selectedEWallet: 'nequi',
        selectedExternalPayProvider: getInitialColombianExternalPayProvider(incomingPayment),
        selectedExternalPayProviderBank: '',
      };
    }

    case 'cl':
      return {
        chileanDocumentType: 'RUT',
        chileanDocument: getDocumentOrEmpty(incomingPayment),
        chileanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        chileanZipcode: getZipcodeOrEmpty(incomingPayment),
        chileanState: getAddressState(getAddress(incomingPayment)),
        chileanCity: getCity(getAddress(incomingPayment)),
        chileanAddress: getStreetName(getAddress(incomingPayment)),
        chileanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        chileanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEWallet: getSelectedEWallet(incomingPayment),
      };

    case 'pe':
      return {
        peruvianDocumentType: peruvianDocumentTypeInferredFromValue(getDocumentOrEmpty(incomingPayment)),
        peruvianDocument: getDocumentOrEmpty(incomingPayment),
        peruvianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        peruvianZipcode: getZipcodeOrEmpty(incomingPayment),
        peruvianState: getAddressState(getAddress(incomingPayment)),
        peruvianCity: getCity(getAddress(incomingPayment)),
        peruvianAddress: getStreetName(getAddress(incomingPayment)),
        peruvianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        peruvianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEWallet: 'yape',
      };

    case 'ec':
      return {
        ecuadorianDocument: getDocumentOrEmpty(incomingPayment),
        ecuadorianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        ecuadorianZipcode: getZipcodeOrEmpty(incomingPayment),
        ecuadorianState: getAddressState(getAddress(incomingPayment)),
        ecuadorianCity: getCity(getAddress(incomingPayment)),
        ecuadorianAddress: getStreetName(getAddress(incomingPayment)),
        ecuadorianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        ecuadorianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };

    case 'bo':
      return {
        bolivianDocument: getDocumentOrEmpty(incomingPayment),
        bolivianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        bolivianZipcode: getZipcodeOrEmpty(incomingPayment),
        bolivianState: getAddressState(getAddress(incomingPayment)),
        bolivianCity: getCity(getAddress(incomingPayment)),
        bolivianAddress: getStreetName(getAddress(incomingPayment)),
        bolivianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        bolivianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };

    case 'uy': {
      return {
        uruguayanDocumentType: 'CI',
        uruguayanDocument: getDocumentOrEmpty(incomingPayment),
        uruguayanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        uruguayanZipcode: getZipcodeOrEmpty(incomingPayment),
        uruguayanState: getAddressState(getAddress(incomingPayment)),
        uruguayanCity: getCity(getAddress(incomingPayment)),
        uruguayanAddress: getStreetName(getAddress(incomingPayment)),
        uruguayanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        uruguayanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEWallet: getSelectedEWallet(incomingPayment),
      };
    }

    case 'cr': {
      return {
        costaRicanDocument: getDocumentOrEmpty(incomingPayment),
        costaRicanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        costaRicanZipcode: getZipcodeOrEmpty(incomingPayment),
        costaRicanState: getAddressState(getAddress(incomingPayment)),
        costaRicanCity: getCity(getAddress(incomingPayment)),
        costaRicanAddress: getStreetName(getAddress(incomingPayment)),
        costaRicanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        costaRicanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };
    }

    case 'py': {
      return {
        paraguayanDocument: getDocumentOrEmpty(incomingPayment),
        paraguayanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        paraguayanZipcode: getZipcodeOrEmpty(incomingPayment),
        paraguayanState: getAddressState(getAddress(incomingPayment)),
        paraguayanCity: getCity(getAddress(incomingPayment)),
        paraguayanAddress: getStreetName(getAddress(incomingPayment)),
        paraguayanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        paraguayanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };
    }

    case 'pa': {
      return {
        panamanianDocument: getDocumentOrEmpty(incomingPayment),
        panamanianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        panamanianZipcode: getZipcodeOrEmpty(incomingPayment),
        panamanianState: getAddressState(getAddress(incomingPayment)),
        panamanianCity: getCity(getAddress(incomingPayment)),
        panamanianAddress: getStreetName(getAddress(incomingPayment)),
        panamanianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        panamanianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };
    }

    case 'sv': {
      return {
        elSalvadorDocument: getDocumentOrEmpty(incomingPayment),
        elSalvadorPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        elSalvadorZipcode: getZipcodeOrEmpty(incomingPayment),
        elSalvadorState: getAddressState(getAddress(incomingPayment)),
        elSalvadorCity: getCity(getAddress(incomingPayment)),
        elSalvadorAddress: getStreetName(getAddress(incomingPayment)),
        elSalvadorStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        elSalvadorStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };
    }

    case 'gt': {
      return {
        guatemalanDocument: getDocumentOrEmpty(incomingPayment),
        guatemalanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        guatemalanZipcode: getZipcodeOrEmpty(incomingPayment),
        guatemalanState: getAddressState(getAddress(incomingPayment)),
        guatemalanCity: getCity(getAddress(incomingPayment)),
        guatemalanAddress: getStreetName(getAddress(incomingPayment)),
        guatemalanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        guatemalanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };
    }

    case 'do': {
      return {
        dominicanDocument: getDocumentOrEmpty(incomingPayment),
        dominicanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        dominicanZipcode: getZipcodeOrEmpty(incomingPayment),
        dominicanState: getAddressState(getAddress(incomingPayment)),
        dominicanCity: getCity(getAddress(incomingPayment)),
        dominicanAddress: getStreetName(getAddress(incomingPayment)),
        dominicanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        dominicanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };
    }

    case 'za': {
      return {
        southAfricanDocument: getDocumentOrEmpty(incomingPayment),
        southAfricanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        southAfricanZipcode: getZipcodeOrEmpty(incomingPayment),
        southAfricanState: getAddressState(getAddress(incomingPayment)),
        southAfricanCity: getCity(getAddress(incomingPayment)),
        southAfricanAddress: getStreetName(getAddress(incomingPayment)),
        southAfricanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        southAfricanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEWallet: 'capitec-pay',
      };
    }

    case 'ke': {
      return {
        kenyanDocument: getDocumentOrEmpty(incomingPayment),
        kenyanPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'ke'),
        kenyanZipcode: getZipcodeOrEmpty(incomingPayment),
        kenyanState: getAddressState(getAddress(incomingPayment)),
        kenyanCity: getCity(getAddress(incomingPayment)),
        kenyanAddress: getStreetName(getAddress(incomingPayment)),
        kenyanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        kenyanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEWallet: 'mpesa',
      };
    }

    case 'ng': {
      return {
        nigerianPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'ng'),
        nigerianAccountNumber: '',
        selectedEWallet: 'ussd',
      };
    }

    case 'in': {
      return {
        indiaCustomerName: getCustomerName(incomingPayment),
        indiaPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        indiaZipcode: getZipcodeOrEmpty(incomingPayment),
        indiaState: getAddressState(getAddress(incomingPayment)),
        indiaCity: getCity(getAddress(incomingPayment)),
        indiaStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        indiaStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        indiaAddress: getStreetName(getAddress(incomingPayment)),
        indiaUpiId: '',
        indiaUpiApp: '',
        selectedEWallet: 'upi-qrcode',
      };
    }

    case 'ph': {
      return {
        philippinesPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        selectedEWallet: getPaymentTypeCode(incomingPayment),
      };
    }

    case 'tz': {
      return {
        tanzanianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        selectedEWallet: 'airtel',
      };
    }

    case 'ug': {
      return {
        ugandaPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'ug'),
        selectedEWallet: 'airtel',
      };
    }

    case 'zm': {
      return {
        zambiaPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'zm'),
        selectedEWallet: 'airtel',
      };
    }

    case 'eg': {
      return {
        egyptPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
      };
    }

    case 'jm': {
      return {
        jamaicaPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
      };
    }

    case 'gh': {
      return {
        ghanaPhoneNumber: maskValue(getPhoneNumberOrEmptyGhana(incomingPayment) || '', 'gh'),
        selectedEWallet: 'airtel',
      };
    }

    case 'tr': {
      return {
        turkeyPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'tr'),
      };
    }

    case 'th': {
      return {
        thaiPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        selectedEWallet: 'mobile-banking',
        selectedBankCode: '',
      };
    }

    case 'id': {
      return {
        indonesianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        selectedVirtualAccountBank: '',
        selectedEWallet: 'dana',
      };
    }

    default:
      throw new Error(`Country ${getCountry(incomingPayment)} not supported`);
  }
}

function getDocumentOrEmpty(incomingPayment: IncomingPayment): string {
  return getDocument(incomingPayment) || '';
}

function getPhoneNumberOrEmpty(incomingPayment: IncomingPayment): string {
  return getPhoneNumber(incomingPayment) || '';
}

function getZipcodeOrEmpty(incomingPayment: IncomingPayment): string {
  return getZipcode(getAddress(incomingPayment)) || '';
}

function getPhoneNumberOrEmptyGhana(incomingPayment): ?string {
  return (!incomingPayment.hasOwnProperty('status_otp')) ? getPhoneNumber(incomingPayment) : '';
}

function getSelectedEWallet(incomingPayment: IncomingPayment) {
  const paymentTypeCode = getPaymentTypeCode(incomingPayment);

  switch (getCountry(incomingPayment)) {
    case 'ar':
      return paymentTypeCode === 'mpconnect' ? paymentTypeCode : 'mercadopago';
    case 'cl':
      return paymentTypeCode === 'mpconnect' ? paymentTypeCode : 'machpay';
    case 'uy':
      return paymentTypeCode === 'mpconnect' ? paymentTypeCode : undefined;
    default:
      return undefined;
  }
}

function getInitialColombianExternalPayProvider(incomingPayment: IncomingPayment): string {
  const paymentTypeCode = getPaymentTypeCode(incomingPayment);

  return (paymentTypeCode === 'achpse' || paymentTypeCode === 'bancolombia-button')
    ? paymentTypeCode
    : '';
}

export function getPendingPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: getPaymentType(incomingPayment),
  };
}

export function getConfirmedPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: getPaymentType(incomingPayment),
  };
}

export function getCanceledPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: getPaymentType(incomingPayment),
    selectedInstalment: getLowerInstalmentKey(getReceipts(incomingPayment)),
    eligible: incomingPayment['eligible'],
  };
}


export function getPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  switch (incomingPayment.status) {
    case 'OP':
      return getOpenedPaymentBasedInitialUserInputValues(incomingPayment);

    case 'PE':
      return getPendingPaymentBasedInitialUserInputValues(incomingPayment);

    case 'CO':
      return getConfirmedPaymentBasedInitialUserInputValues(incomingPayment);

    case 'CA':
      return getCanceledPaymentBasedInitialUserInputValues(incomingPayment);

    default:
      return {};
  }
}
